import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';
import type { SubscriptionSearchListItem } from '../../@types/AdvertismentSubscription';
import type { ElasticSearchResponse } from '../../@types/ElasticSearchResponse';
import type { ElasticVehicle } from '../../@types/ElasticVehicle';
import { all as getBrandings } from '../../actions/branding';
import { get as getContentPage } from '../../actions/contentpage';
import { all as getList } from '../../actions/list';
import { callbackSearchFull, clear, facets as getFacets, search } from '../../actions/search';
import { create, remove, update } from '../../actions/search-subscription';
import {
  trackLoginModuleClickLoginSaveCategory as _trackLoginModuleClickLoginSaveCategory,
  trackLoginModuleInViewportSaveCategory as _trackLoginModuleInViewportSaveCategory,
} from '../../actions/tracking';
import TypeFilterBar from '../../components/filters/type-filter-bar';
import FloatingActionLink from '../../components/floating-action/floating-action-link';
import HeroShell from '../../components/hero/hero-shell';
import { PlaceholderResultSet, ResultSet } from '../../components/search';
import PaginationV2 from '../../components/search/pagination-v2';
import { events, QueryFilter, text } from '../../helpers';
import { queryFilterImport } from '../../helpers/query-filter';
import { ExchangeTypes, useDataExchangeStatus } from '../../hooks/exchange-status';
import useQueryFilterFromUrl from '../../hooks/useQueryFilterFromUrl';
import useTrackPageView from '../../hooks/useTrackPageView';
import type { ParsedList } from '../../models/list';
import type { RootState } from '../../reducers';
import { isAuthenticated } from '../../selectors/auth';
import { getCurrentListPage, getCurrentListPageQueryFilter } from '../../selectors/list';
import { getSubscription } from '../../selectors/subscriptions';
import { Button } from '../../styled-components/components/Button';
import Content from '../../styled-components/components/Content';
import { IconHeart, IconHeartFilled } from '../../styled-components/components/Icon';
import Loader from '../../styled-components/components/Loader';
import Modal from '../../styled-components/components/Modal';
import { Text } from '../../styled-components/components/Text';
import SignInModal from '../../styled-components/structures/Modals/SignInModal';
import ShareButton from '../../styled-components/structures/ShareButton';

const textSize = 140;
const formatText = (s: string) => {
  if (s.length > textSize + 3) {
    return {
      expandable: true,
      formatteddescription: `${s.slice(0, textSize).trim()}...`,
    };
  }

  return {
    expandable: false,
    formatteddescription: s,
  };
};

interface ListPageProps {
  slug: string;
}

const ListPage = ({ slug: _slug }: ListPageProps) => {
  const { t } = useTranslation(['common', 'listPage']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const requestStatus = useSelector<RootState, number>((s) => s.subscriptions.search.requestStatus);
  const responseStatus = useSelector<RootState, number>(
    (s) => s.subscriptions.search.responseStatus,
  );
  const page = useSelector<RootState, ParsedList | null>((s) => getCurrentListPage(s, _slug));
  const _queryFilterRaw = useSelector<RootState, string | null>((s) =>
    getCurrentListPageQueryFilter(s, _slug),
  );

  const _queryFilter = useMemo(() => queryFilterImport(_queryFilterRaw), [_queryFilterRaw]);

  const canFollowList = useSelector(isAuthenticated);
  const subscription = useSelector<RootState, SubscriptionSearchListItem | null>((s) =>
    getSubscription(s, _slug),
  );
  const qf = useQueryFilterFromUrl(_slug);

  const queryFilter = useMemo(() => (qf.isEmpty() ? _queryFilter : qf), [_queryFilter, qf]);

  const actions = bindActionCreators(
    {
      getContentPage,
      getList,
      getBrandings,
      getFacets,
      search,
      clear,
      create,
      remove,
      update,
      trackLoginModuleInViewportSaveCategory: _trackLoginModuleInViewportSaveCategory,
      trackLoginModuleClickLoginSaveCategory: _trackLoginModuleClickLoginSaveCategory,
    },
    dispatch,
  );

  const [readMore, setReadMore] = useState(false);
  const [isSearching, setIsSearching] = useState(true);
  const [state, setState] = useState<ElasticSearchResponse<ElasticVehicle> | undefined>();
  const [signUpModal, setSignUpModal] = useState(false);
  const scrollRefTarget = useRef<HTMLDivElement | null>(null);

  const reqFetch = useDataExchangeStatus(ExchangeTypes.Fetching, [requestStatus, responseStatus]);
  const reqCreate = useDataExchangeStatus(ExchangeTypes.Creating, [requestStatus, responseStatus]);
  const reqRemove = useDataExchangeStatus(ExchangeTypes.Removing, [requestStatus, responseStatus]);
  const trackPageViewEvent = useTrackPageView();

  const pending = reqFetch.pending || reqCreate.pending || reqRemove.pending;

  const onSearch = (qff: QueryFilter) => {
    setIsSearching(true);
    callbackSearchFull(qff, (result) => {
      setState(result);
      setIsSearching(false);
    });
  };

  useEffect(() => {
    onSearch(queryFilter);
  }, [queryFilter.toString()]);

  useEffect(() => {
    if (subscription && canFollowList) {
      actions.update({
        ...subscription,
        numberOfHits: 0,
      });
    }
  }, [!!subscription && !!canFollowList]);

  useEffect(() => {
    if (!page) actions.getList();

    if (page) document.title = text.createPageTitle(page.title);
    setTimeout(events.resetViewport, 10);
  }, []);

  useEffect(() => {
    if (page) {
      trackPageViewEvent();
    }
  }, [page]);

  const shouldScrollView = () => {
    const scrollTarget =
      scrollRefTarget && scrollRefTarget.current
        ? scrollRefTarget.current.getBoundingClientRect()
        : null;
    if (scrollTarget && scrollTarget.top < 0 && document.body && document.documentElement) {
      document.body.scrollTop = document.documentElement.scrollTop =
        scrollTarget.top + document.documentElement.scrollTop;
    }
  };

  const onRequestSearch = (qff: QueryFilter) => {
    shouldScrollView();
    navigate(`${_slug}${new QueryFilter(qff.toString()).toQueryURL()}`);
  };

  const followList = () => {
    if (!canFollowList) {
      setSignUpModal(true);
    } else if (page) {
      actions.create({
        referenceId: page.id,
        name: page.title,
        query: page.queryFilter.toString(),
      });
    }
  };

  const unFollowList = () => {
    if (subscription) {
      actions.remove(subscription);
    }
  };

  const onApplyFilter = (filterQuery: string) => {
    shouldScrollView();
    onRequestSearch(new QueryFilter(filterQuery));
  };

  if (!page) {
    return (
      <div className="m-t">
        <Loader />
      </div>
    );
  }

  const { image, title, description, slug } = page;

  const { expandable, formatteddescription } = formatText(description);

  return (
    <>
      {signUpModal && (
        <SignInModal
          image="/images/modal/list_page.png"
          title={t('mustLoginToFollowCategory', { ns: 'listPage' })}
          text={t('loginModalDescription', { ns: 'loginModal' })}
          onClose={() => setSignUpModal(false)}
          trackOnInit={actions.trackLoginModuleInViewportSaveCategory}
          trackOnSignInClick={actions.trackLoginModuleClickLoginSaveCategory}
        />
      )}
      {readMore && (
        <Modal heading={title} onClose={() => setReadMore(false)}>
          <Content>
            <p>{description}</p>
          </Content>
        </Modal>
      )}
      <HeroShell
        modifiers="content-center text-center"
        parallax={true}
        imageDesktop={image || undefined}
        imageMobile={image || undefined}
      >
        <>
          <div className="hero-content-body">
            {state && (
              <div data-wx-label="light" className="m-b-half">
                {t('numberOfCars', { ns: 'listPage', count: state.stats?.totalHits ?? 0 })}
              </div>
            )}
            <h1 className="m-b">{title}</h1>
            <div className="font-medium" style={{ whiteSpace: 'pre-wrap' }}>
              {formatteddescription}
            </div>
            {expandable && (
              <div className="m-t">
                <button
                  type="button"
                  data-wx-button="action-light thin"
                  title={t('readMore', { ns: 'common' })}
                  onClick={() => setReadMore(true)}
                >
                  {t('readMore', { ns: 'common' })}
                </button>
              </div>
            )}
          </div>
          <div className="hero-content-footer">
            <div className="hero-content-actions">
              <div className="hero-content-action">
                <ShareButton />
              </div>
              <div className="hero-content-action">
                {!subscription && (
                  <Button
                    $shadow
                    $fullWidth
                    $size="small"
                    title={t('follow', { ns: 'listPage' })}
                    onClick={followList}
                    disabled={pending}
                  >
                    <Text $tone="secondary" aria-hidden="true">
                      <IconHeart $size="20px" />
                    </Text>
                    {t('follow', { ns: 'listPage' })}
                  </Button>
                )}
                {canFollowList && subscription && (
                  <Button
                    $shadow
                    $fullWidth
                    $size="small"
                    title={t('stopFollowing', { ns: 'listPage' })}
                    onClick={unFollowList}
                    disabled={pending}
                  >
                    <Text $tone="secondary" aria-hidden="true">
                      <IconHeartFilled $size="20px" />
                    </Text>
                    {t('following', { ns: 'listPage' })}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      </HeroShell>
      {state && queryFilter && (
        <div data-wx-page="" className="container" ref={scrollRefTarget}>
          <TypeFilterBar
            queryFilter={queryFilter}
            onRequestSearch={onRequestSearch}
            sortOptions={state.documentList.sortOptions}
            onApplySort={onApplyFilter}
          />
          {isSearching ? (
            <section className="page-section">
              <PlaceholderResultSet size={12} />
            </section>
          ) : (
            <>
              <section className="page-section">
                <ResultSet
                  data={state.documentList.documents}
                  title={page.title}
                  disableScrollTopButton={true}
                />
              </section>
              <section className="page-section">
                <PaginationV2
                  pages={state.documentList.pagination?.pages}
                  target={`/${_slug}`}
                  onScrollTop={shouldScrollView}
                />
              </section>
            </>
          )}
          <FloatingActionLink
            title={t('adjustWithOwnFilter', { ns: 'listPage' })}
            onBeforeNavigate={() => actions.search(queryFilter)}
            to={`/sok${queryFilter.toQueryURL()}&list=${slug}`}
            icon="icon-arrow-right"
          />
        </div>
      )}
    </>
  );
};

export default ListPage;
